import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Circle, Loader2, Mic2, RotateCcw, Save, Sparkles, Square, Wand2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import CreatorPlayer from "@/components/creator/CreatorPlayer";
import SongCover from "@/components/creator/SongCover";
import MicPicker, { useSavedMic } from "@/components/creator/MicPicker";
import useCreatorSong from "@/components/creator/useCreatorSong";
import useVocalRecorder from "@/components/creator/useVocalRecorder";
import { LevelMeter, TakeWave, fmtTime as fmt } from "@/components/creator/RecordBits";
import { listSongs, patchSong } from "@/lib/creatorApi";
import { listProjects } from "@/lib/studioApi";
import { ensureVocalTakeLoaded, uploadVocalTake, vocalFileUrl } from "@/lib/creatorVocal";
import { getCurrentSongId, proStudioPath } from "@/lib/studioMode";

function ProjectPicks() {
  const [projects, setProjects] = useState(null);
  useEffect(() => { listProjects({ limit: 12 }).then((rows) => setProjects(rows.filter((p) => p.versions_count))).catch(() => setProjects([])); }, []);
  if (!projects?.length) return null;
  return (
    <div className="mb-10">
      <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-rose-200/80">Sing it myself · your projects</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="record-picker-projects">
        {projects.map((p) => (
          <Link key={p.id} to={`/create/project/${p.id}/sing`} data-testid={`record-pick-project-${p.id}`} className="flex items-center gap-3 rounded-2xl border border-rose-300/20 bg-rose-500/[0.06] p-3 transition-colors hover:bg-rose-500/[0.12]">
            <SongCover song={p} size="sm" />
            <span className="min-w-0"><span className="block truncate font-display text-sm font-semibold">{p.title || "Untitled song"}</span><span className="block text-[11px] text-white/45">{p.genre_label} · {p.arrangement_label}</span></span>
          </Link>
        ))}
      </div>
      <p className="mt-6 mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">Or sing over any song</p>
    </div>
  );
}

function RecordPicker() {
  const [songs, setSongs] = useState(null);
  useEffect(() => { listSongs({ limit: 30 }).then(setSongs).catch(() => setSongs([])); }, []);
  return (
    <CreatorShell testId="record-picker-page" back="/create" spine="record" title="What do you want to sing over?" subtitle="Pick one of your songs, or record your voice on its own.">
      <div className="mb-6">
        <Link to="/create/record/solo" data-testid="record-solo" className="inline-flex items-center gap-2 rounded-full border border-rose-400/40 bg-rose-500/15 px-5 py-3 font-display text-sm font-semibold text-rose-100 transition-colors hover:bg-rose-500/25">
          <Mic2 className="h-4 w-4" /> Record without a song <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <ProjectPicks />
      {songs === null ? <Loader2 className="h-5 w-5 animate-spin text-white/60" /> : songs.length === 0 ? (
        <Link to="/create/song" data-testid="record-picker-create" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-3 font-display font-semibold">Create a song first <ArrowRight className="h-4 w-4" /></Link>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="record-picker-list">
          {songs.map((s) => (
            <Link key={s.id} to={`/create/record/${s.id}`} data-testid={`record-pick-${s.id}`} className="flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 p-3 transition-colors hover:bg-white/[0.06]">
              <SongCover song={s} size="sm" />
              <span className="min-w-0"><span className="block truncate font-display text-sm font-semibold">{s.title}</span><span className="block text-[11px] text-white/45">{s.vocal ? "Has your voice ✓" : s.creator_meta?.style_label || "Song"}</span></span>
            </Link>
          ))}
        </div>
      )}
    </CreatorShell>
  );
}

/** Record Your Vocal — one click. */
export default function RecordVocalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const current = getCurrentSongId();
  useEffect(() => { if (!id && current) navigate(`/create/record/${current}`, { replace: true }); }, [id, current, navigate]);
  if (!id) return current ? null : <RecordPicker />;
  return <RecordSession key={id} id={id} />;
}

function RecordSession({ id }) {
  const solo = id === "solo";
  const navigate = useNavigate();
  const { engine, song, setSong, error, channel: songChannel, loadState } = useCreatorSong(solo ? null : id);
  const [deviceId, setDeviceId] = useSavedMic();
  const songBuffer = songChannel != null ? engine.channels[songChannel]?.buffer : null;
  const songDuration = songBuffer?.duration || song?.duration || 0;
  const title = solo ? "My voice" : song?.title || "Song";
  const rec = useVocalRecorder({ engine, hasBacking: !solo, songDuration, takeName: `${title} · my voice`, deviceId });
  const [keeping, setKeeping] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [voiceOnly, setVoiceOnly] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const mutedByUs = useRef(new Set());

  useEffect(() => {
    if (rec.phase !== "recording") return undefined;
    const t = setInterval(() => setPlayhead(engine.getPlayhead()), 200);
    return () => clearInterval(t);
  }, [rec.phase, engine]);

  const applyVoiceOnly = (on) => {
    engine.channels.forEach((c, i) => {
      if (i === rec.vocalChannel || !c?.buffer) return;
      if (on) { if (!c.muted) { mutedByUs.current.add(i); engine.setChannelMute(i, true); } }
      else if (mutedByUs.current.has(i)) engine.setChannelMute(i, false);
    });
    if (!on) mutedByUs.current.clear();
    setVoiceOnly(on);
  };
  useEffect(() => () => { mutedByUs.current.forEach((i) => engine.setChannelMute(i, false)); }, [engine]);

  const ready = solo || loadState === "ready";
  const canRestore = !solo && song?.vocal && rec.takes.length === 0 && rec.phase === "idle";

  const restore = async () => {
    setRestoring(true);
    try {
      const idx = await ensureVocalTakeLoaded(engine, song);
      if (idx != null) rec.adoptChannel(idx, engine.channels[idx].buffer, song.vocal);
    } catch (e) { toast.error("Couldn't load your recording", { description: e?.message }); }
    finally { setRestoring(false); }
  };

  const keep = async () => {
    const take = rec.activeTake;
    if (!take || keeping) return;
    setKeeping(true);
    try {
      const label = `${title} — my voice`;
      let saved = null;
      if (take.blob) saved = await uploadVocalTake(take.blob, label);
      if (!solo && song) {
        const vocal = {
          file_url: saved?.file_url || song.vocal?.file_url, recording_id: saved?.id || take.recordingId || song.vocal?.recording_id,
          trim_head_sec: take.headSec, duration_sec: take.durationSec, at: take.at, label, channel: rec.vocalChannel,
        };
        const updated = await patchSong(song.id, { vocal });
        setSong(updated);
        // Tag the live channel so the Pro hand-off recognises it instead of loading the file twice.
        const ch = rec.vocalChannel != null ? engine.channels[rec.vocalChannel] : null;
        if (ch?.buffer && updated.vocal) ch.sourceUrl = vocalFileUrl(updated.vocal);
      }
      rec.markKept(take.id, saved?.id || take.recordingId);
      toast.success("Your voice is saved", { description: solo ? "Find it in My Projects → Recordings." : "It stays with this song — find it in My Projects too." });
    } catch (e) {
      toast.error("Couldn't save the take", { description: e?.response?.data?.detail || e?.message || "Try again." });
    } finally { setKeeping(false); }
  };

  const betterPath = solo ? proStudioPath(null, "fx") + (rec.vocalChannel != null ? `&ch=${rec.vocalChannel}` : "") : `${proStudioPath(song?.id, "fx")}&ch=${rec.vocalChannel ?? ""}`;

  if (error) return <CreatorShell testId="record-page" back="/create/projects" title="Hmm, that song isn't here" subtitle={error} />;

  const phase = rec.phase;
  const progress = songDuration > 0 ? Math.min(100, (playhead / songDuration) * 100) : 0;

  return (
    <CreatorShell testId="record-page" back={solo ? "/create" : `/create/song/${id}`} spine="record" spineDone={solo ? [] : ["write", "generate"]}>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="min-w-0 space-y-5">
          <div className="flex items-center gap-4">
            {solo ? <span className="grid h-24 w-24 place-items-center rounded-2xl bg-gradient-to-br from-rose-500 to-red-500"><Mic2 className="h-9 w-9" /></span> : <SongCover song={song} size="md" />}
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">{solo ? "No backing track" : "Singing over"}</p>
              <p data-testid="record-song-title" className="truncate font-display text-lg font-semibold">{title}</p>
              {!solo && songDuration ? <p className="text-[12px] text-white/45">{fmt(songDuration)}</p> : null}
            </div>
          </div>
          <MicPicker deviceId={deviceId} onChange={setDeviceId} disabled={phase === "recording" || phase === "arming"} />
        </aside>

        <section className="min-w-0">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-rose-300/90"><Mic2 className="h-3.5 w-3.5" /> Record your vocal</p>
          <h1 data-testid="creator-title" className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
            {phase === "recording" ? "Sing!" : phase === "review" ? "How did that sound?" : "Ready when you are"}
          </h1>
          <p className="mt-3 text-white/60 md:text-lg">
            {phase === "recording" ? "The song is playing — we're recording your voice. Press stop when you're done." : phase === "review" ? "Play it back. Keep it, or record again — you can't break anything." : solo ? "Press the button and start singing. Press it again to stop." : "Press the button — the song starts from the top and we record your voice over it."}
          </p>

          {phase === "idle" || phase === "arming" ? (
            <div className="mt-10 flex flex-col items-start gap-4">
              <button
                type="button"
                data-testid="record-start-btn"
                onClick={rec.start}
                disabled={!ready || phase === "arming"}
                className="group inline-flex items-center gap-4 rounded-full bg-gradient-to-r from-rose-500 to-red-600 px-9 py-5 font-display text-lg font-bold tracking-wide text-white shadow-[0_14px_44px_rgba(244,63,94,0.45)] transition-transform duration-200 hover:scale-[1.03] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100"
              >
                {phase === "arming" ? <Loader2 className="h-6 w-6 animate-spin" /> : <span className="relative grid h-6 w-6 place-items-center"><span className="absolute inset-0 animate-ping rounded-full bg-white/40" /><Circle className="h-4 w-4 fill-white text-white" /></span>}
                {phase === "arming" ? "OPENING MICROPHONE…" : "START RECORDING"}
              </button>
              {!ready ? <p data-testid="record-loading-hint" className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/40">Loading the song…</p> : null}
              {canRestore ? (
                <button type="button" data-testid="record-restore-btn" onClick={restore} disabled={restoring} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-white/70 hover:text-white disabled:opacity-50">
                  {restoring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Load my last recording
                </button>
              ) : null}
            </div>
          ) : null}

          {phase === "recording" || phase === "processing" ? (
            <div data-testid="record-live" className="mt-10 rounded-3xl border border-rose-400/30 bg-rose-500/[0.07] p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="relative grid h-4 w-4 place-items-center"><span className="absolute inset-0 animate-ping rounded-full bg-rose-400/60" /><span className="h-3 w-3 rounded-full bg-rose-500" /></span>
                  <span data-testid="record-elapsed" className="font-mono text-3xl tabular-nums">{fmt(rec.elapsed)}</span>
                  {!solo && songDuration ? <span className="font-mono text-sm text-white/45">/ {fmt(songDuration)}</span> : null}
                </div>
                <button type="button" data-testid="record-stop-btn" onClick={rec.stop} disabled={phase !== "recording"} className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-display text-sm font-bold text-black transition-transform hover:scale-[1.03] disabled:opacity-50">
                  {phase === "processing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4 fill-black" />} {phase === "processing" ? "Saving your take…" : "STOP RECORDING"}
                </button>
              </div>
              {!solo ? <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.08]"><div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-violet-400 transition-[width] duration-200" style={{ width: `${progress}%` }} /></div> : null}
              <div className="mt-4">
                <p className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">Your voice</p>
                <LevelMeter level={rec.level} />
              </div>
            </div>
          ) : null}

          {phase === "review" && rec.activeTake ? (
            <div data-testid="record-review" className="mt-10 space-y-6">
              <div className="rounded-3xl border border-white/[0.08] bg-black/25 p-5">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Your take · {fmt(rec.activeTake.durationSec)} {rec.activeTake.kept ? <span className="ml-2 text-emerald-300">Saved ✓</span> : null}</p>
                  {rec.takes.length > 1 ? (
                    <div data-testid="record-takes" className="flex flex-wrap gap-1.5">
                      {rec.takes.map((t, i) => (
                        <button key={t.id} type="button" data-testid={`record-take-${i + 1}`} aria-pressed={t.id === rec.activeTake.id} onClick={() => rec.selectTake(t.id)} className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider ${t.id === rec.activeTake.id ? "border-rose-400/60 bg-rose-500/20 text-white" : "border-white/10 text-white/55 hover:text-white"}`}>
                          Take {i + 1}{t.kept ? " ✓" : ""}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <TakeWave buffer={rec.activeTake.buffer} />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Play recording</p>
                  {!solo ? (
                    <div role="radiogroup" className="inline-flex rounded-full border border-white/10 bg-black/40 p-1">
                      <button type="button" role="radio" aria-checked={!voiceOnly} data-testid="record-hear-mix" onClick={() => applyVoiceOnly(false)} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider ${!voiceOnly ? "bg-white/[0.12] text-white" : "text-white/50"}`}>Song + voice</button>
                      <button type="button" role="radio" aria-checked={voiceOnly} data-testid="record-hear-voice" onClick={() => applyVoiceOnly(true)} className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider ${voiceOnly ? "bg-white/[0.12] text-white" : "text-white/50"}`}>Voice only</button>
                    </div>
                  ) : null}
                </div>
                <CreatorPlayer engine={engine} channel={solo ? rec.vocalChannel : songChannel} loadState="ready" />
              </div>

              <div data-testid="record-review-actions" className="flex flex-wrap items-center gap-3">
                <button type="button" data-testid="record-keep-btn" onClick={keep} disabled={keeping || rec.activeTake.kept} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 px-7 py-3.5 font-display text-base font-bold text-black shadow-[0_10px_30px_rgba(52,211,153,0.35)] transition-transform hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100">
                  {keeping ? <Loader2 className="h-5 w-5 animate-spin" /> : rec.activeTake.kept ? <Check className="h-5 w-5" /> : <Save className="h-5 w-5" />} {rec.activeTake.kept ? "Kept" : "Keep It"}
                </button>
                <button type="button" data-testid="record-again-btn" onClick={rec.start} className="inline-flex items-center gap-2 rounded-full border border-rose-400/40 bg-rose-500/15 px-6 py-3.5 font-display text-base font-semibold text-rose-100 transition-colors hover:bg-rose-500/25">
                  <RotateCcw className="h-5 w-5" /> Record Again
                </button>
              </div>

              {rec.activeTake.kept ? (
                <div data-testid="record-next" className="rounded-3xl border border-violet-400/25 bg-violet-500/[0.08] p-5">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-violet-200"><Sparkles className="h-3.5 w-3.5" /> Next step</p>
                  <button type="button" data-testid="record-make-better-btn" onClick={() => navigate(betterPath)} className="mt-3 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-8 py-4 font-display text-base font-bold text-white shadow-[0_12px_36px_rgba(99,102,241,0.4)] transition-transform hover:scale-[1.02]">
                    <Wand2 className="h-5 w-5" /> MAKE MY VOCAL SOUND BETTER
                  </button>
                  <p className="mt-2 text-[12px] text-white/50">Opens your voice in the studio's vocal effects — one-tap Vocal Sound presets are coming next.</p>
                  {!solo ? <Link to={`/create/song/${id}`} data-testid="record-back-to-song" className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white/50 hover:text-white">Back to your song <ArrowRight className="h-3.5 w-3.5" /></Link> : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>
    </CreatorShell>
  );
}
